import { defineStore } from "pinia";
import { uiStore } from "./UiStore";
import dayjs from "dayjs";

export const connectionStore = defineStore("connection", () => {
  const useUiStore = uiStore();
  let virtualServiceRequestCache = null;
  const usePrivilegeApi =
    String(process.env.VUE_APP_USE_PRIVILEGE_API ?? "false").toLowerCase() ===
    "true";
  const privilegeApiBaseUrl = String(
    process.env.VUE_APP_PRIVILEGE_API_BASE_URL ??
      process.env.VUE_APP_API_BASE_URL ??
      ""
  ).trim();
  const shouldShowMockModal =
    String(process.env.VUE_APP_SHOW_MOCK_MODAL ?? "false").toLowerCase() ===
    "true";

  const createWeatherMockList = () => {
    const weatherCodes = ["Clear", "Clouds", "Rain", "Snow", "Mist"];
    const list = [];
    const now = new Date();

    for (let day = 0; day < 5; day++) {
      for (let hour = 0; hour < 24; hour += 3) {
        const date = new Date(now);
        date.setDate(now.getDate() + day);
        date.setHours(hour, 0, 0, 0);
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, "0");
        const dd = String(date.getDate()).padStart(2, "0");
        const hh = String(date.getHours()).padStart(2, "0");

        list.push({
          dt_txt: `${yyyy}-${mm}-${dd} ${hh}:00:00`,
          main: {
            temp: 18 + day + hour / 24,
            feels_like: 17 + day + hour / 24,
          },
          weather: [{ main: weatherCodes[(day + hour / 3) % weatherCodes.length] }],
        });
      }
    }

    return list;
  };

  const createMasterDataMock = (masterdataKey) => {
    const source = {
      DEL_YN: [
        { code: "ALL", value1: "ALL", sortSeq: 0 },
        { code: "y", value1: "Y", sortSeq: 1 },
        { code: "n", value1: "N", sortSeq: 2 },
      ],
      ACTIVATION_STATUS: [
        { code: "ALL", value1: "ALL", sortSeq: 0 },
        { code: "y", value1: "Y", sortSeq: 1 },
        { code: "n", value1: "N", sortSeq: 2 },
      ],
      LOCALE: [
        { code: "ko_kr", value1: "Korean", sortSeq: 0 },
        { code: "en_gn", value1: "English", sortSeq: 1 },
      ],
      ACCOUNT_STATUS: [
        { code: "ALL", value1: "ALL", sortSeq: 0 },
        { code: "0001", value1: "0001", sortSeq: 1 },
        { code: "0010", value1: "0010", sortSeq: 2 },
        { code: "0020", value1: "0020", sortSeq: 3 },
        { code: "0040", value1: "0040", sortSeq: 4 },
        { code: "4000", value1: "4000", sortSeq: 5 },
        { code: "9999", value1: "9999", sortSeq: 6 },
      ],
      SERVICE_TYPE: [
        { code: "ALL", value1: "ALL", sortSeq: 0 },
        { code: "0010", value1: "0010", sortSeq: 1 },
        { code: "0020", value1: "0020", sortSeq: 2 },
        { code: "0030", value1: "0030", sortSeq: 3 },
        { code: "0040", value1: "0040", sortSeq: 4 },
        { code: "0050", value1: "0050", sortSeq: 5 },
        { code: "0060", value1: "0060", sortSeq: 6 },
      ],
      FAIL_TYPE: [
        { code: "010102", value1: "010102", sortSeq: 0 },
        { code: "010201", value1: "010201", sortSeq: 1 },
        { code: "010501", value1: "010501", sortSeq: 2 },
        { code: "010601", value1: "010601", sortSeq: 3 },
        { code: "010999", value1: "010999", sortSeq: 4 },
      ],
      EQUIPMENT_TYPE: [
        { code: "ALL", value1: "ALL", sortSeq: 0 },
        { code: "L", value1: "L", sortSeq: 1 },
        { code: "S", value1: "S", sortSeq: 2 },
        { code: "W", value1: "W", sortSeq: 3 },
      ],
      ALARM_TYPE: [
        { code: "ALL", value1: "ALL", value2: "0", sortSeq: 0 },
        { code: "10", value1: "10", value2: "0", sortSeq: 1 },
        { code: "20", value1: "20", value2: "0", sortSeq: 2 },
        { code: "30", value1: "30", value2: "0", sortSeq: 3 },
        { code: "40", value1: "40", value2: "0", sortSeq: 4 },
      ],
      ONECLICK_CONTACT: [
        { code: "SEOUL", value1: "Seoul Office", sortSeq: 1 },
        { code: "BUSAN", value1: "Busan Office", sortSeq: 2 },
      ],
      ROLE_TYPE: [
        { code: "ALL", value1: "ALL", sortSeq: 0 },
        { code: "SYSTEM", value1: "SYSTEM", sortSeq: 1 },
        { code: "ACCOUNT", value1: "ACCOUNT", sortSeq: 2 },
        { code: "MANAGER", value1: "MANAGER", sortSeq: 3 },
        { code: "MAJOR", value1: "MAJOR", sortSeq: 4 },
        { code: "USER", value1: "USER", sortSeq: 5 },
      ],
    };

    return source[masterdataKey] ?? [];
  };

  const createVirtualElevatorList = () => {
    const baseLat = 37.575243;
    const baseLon = 127.000107;
    const siteOffsets = [
      { lat: 0, lon: 0 },
      { lat: 0.0012, lon: 0.0009 },
      { lat: -0.0011, lon: 0.0011 },
      { lat: 0.001, lon: -0.0012 },
      { lat: -0.0013, lon: -0.0008 },
    ];

    const units = [];

    siteOffsets.forEach((offset, siteIndex) => {
      const siteNo = siteIndex + 1;
      const prjNo = `VPRJ${String(siteNo).padStart(3, "0")}`;
      const contractCode = `VCNTR-${String(siteNo).padStart(3, "0")}`;
      const buldNm = `Virtual Site ${String(siteNo).padStart(2, "0")}`;
      const siteLat = Number((baseLat + offset.lat).toFixed(6));
      const siteLon = Number((baseLon + offset.lon).toFixed(6));

      for (let car = 1; car <= 4; car++) {
        const hoNo = String(car).padStart(2, "0");
        const idx = siteIndex * 4 + car;
        const isFault = idx % 5 === 0;
        const type = car % 2 === 0 ? "S" : "L";

        units.push({
          contractCode,
          siteNm: buldNm,
          buldNm,
          custNm: `Virtual Customer ${siteNo}`,
          prjNo,
          hoNo,
          carNo: hoNo,
          elevatorNo: `${prjNo}${hoNo}`,
          intgPrjNo: `INTG-${String(siteNo).padStart(3, "0")}`,
          trlineCd: `TR${String(car).padStart(2, "0")}`,
          intgPrjTrlineCdCode: `${prjNo}-${hoNo}`,
          installationPlace: `Car ${hoNo}`,
          address1: `Virtual Address ${siteNo}, Jongno-gu, Seoul`,
          wgslat: siteLat,
          wgslon: siteLon,
          elStatus: isFault ? "N" : "Y",
          acptDate: isFault ? "20260211" : "",
          contractStartDt: "20240101",
          contractEndDt: "20281231",
          bgnDt: "20240101",
          xpirDt: "20281231",
          elLEa: 3,
          elTEa: 4,
          type,
          elvtrDiv: type,
          elvtrSttsNm: isFault ? "FAULT" : "NORMAL",
          installationDe: "20200201",
          applcEnDt: "20270201",
          inspCompanyNm: "Virtual Maintenance Co.",
          inspCompanyTel: "02-1234-5678",
          issueCompanyNm: "Virtual Insurance Co.",
          issueContEnDe: "20271231",
          shuttleMngrNm: `Manager ${siteNo}`,
          mgrValdEndDt: "20271231",
          model: `VX-${siteNo}${hoNo}`,
          frstInstallationDe: "20200201",
          chngDt: "20250115",
          materCont: buldNm,
          wbsNo: `WBS-${prjNo}-${hoNo}`,
        });
      }
    });

    return units;
  };

  const createVirtualContractDetails = (queryparam = {}) => {
    const units = createVirtualElevatorList();
    const groupMap = new Map();

    units.forEach((unit) => {
      const key = unit.contractCode;
      if (!groupMap.has(key)) {
        groupMap.set(key, {
          contractCode: unit.contractCode,
          siteNm: unit.siteNm,
          custNm: unit.custNm,
          bgnDt: unit.bgnDt,
          xpirDt: unit.xpirDt,
          intgPrjTrlineCdCode: `${unit.prjNo}-ALL`,
          intgPrjNo: unit.intgPrjNo,
          trlineCd: unit.trlineCd,
          prjNo: unit.prjNo,
          projectList: [
            {
              siteNm: unit.siteNm,
              prjNo: unit.prjNo,
              contractCode: unit.contractCode,
              carList: [],
            },
          ],
        });
      }

      const entry = groupMap.get(key);
      const project = entry.projectList[0];
      project.carList.push({
        installationPlace: unit.installationPlace,
        hoNo: unit.hoNo,
      });
    });

    let contractList = Array.from(groupMap.values());
    const keyword = String(queryparam.keyword ?? "").trim().toLowerCase();

    if (keyword) {
      contractList = contractList.filter((item) =>
        [item.contractCode, item.siteNm, item.custNm, item.prjNo].some((field) =>
          String(field ?? "")
            .toLowerCase()
            .includes(keyword)
        )
      );
    }

    return { contractList };
  };

  const createVirtualContractCurrentDetails = (queryparam = {}) => {
    const officeNames = ["Seoul", "Busan", "Daegu", "Incheon", "Daejeon"];
    let contractList = createVirtualElevatorList().map((unit, index) => {
      const officeNm = officeNames[index % officeNames.length];
      return {
        siteNm: unit.siteNm,
        contractCode: unit.contractCode,
        custNm: unit.custNm,
        intgPrjTrlineCdCode: unit.intgPrjTrlineCdCode,
        elevatorNo: unit.elevatorNo,
        bgnDt: unit.bgnDt,
        xpirDt: unit.xpirDt,
        intgPrjNo: unit.intgPrjNo,
        trlineCd: unit.trlineCd,
        salesEmplNm: `Sales ${String((index % 5) + 1)}`,
        officeNm,
        miriYn: index % 2 === 0 ? "Y" : "N",
        miriCallYn: index % 3 === 0 ? "Y" : "N",
        miriViewYn: index % 4 === 0 ? "Y" : "N",
        miriRobotYn: index % 5 === 0 ? "Y" : "N",
      };
    });

    const contains = (value, keyword) =>
      String(value ?? "")
        .toLowerCase()
        .includes(String(keyword ?? "").trim().toLowerCase());

    const fieldName = String(queryparam.fieldName ?? "").trim();
    const keyword = String(queryparam.keyword ?? "").trim();
    const clientName = String(queryparam.clientName ?? "").trim();
    const accountName = String(queryparam.accountName ?? "").trim();
    const elevatorNo = String(queryparam.elevatorNo ?? "").trim();
    const officeName = String(queryparam.officeName ?? "ALL")
      .trim()
      .toUpperCase();

    if (fieldName) {
      contractList = contractList.filter((item) => contains(item.siteNm, fieldName));
    }
    if (keyword) {
      contractList = contractList.filter((item) => contains(item.contractCode, keyword));
    }
    if (clientName) {
      contractList = contractList.filter((item) => contains(item.custNm, clientName));
    }
    if (accountName) {
      contractList = contractList.filter((item) =>
        contains(item.salesEmplNm, accountName)
      );
    }
    if (elevatorNo) {
      contractList = contractList.filter((item) =>
        contains(item.elevatorNo, elevatorNo)
      );
    }
    if (officeName && officeName !== "ALL") {
      contractList = contractList.filter(
        (item) => String(item.officeNm).toUpperCase() === officeName
      );
    }

    ["miriYn", "miriCallYn", "miriViewYn", "miriRobotYn"].forEach((key) => {
      const value = String(queryparam[key] ?? "ALL")
        .trim()
        .toUpperCase();
      if (value && value !== "ALL") {
        contractList = contractList.filter(
          (item) => String(item[key]).toUpperCase() === value
        );
      }
    });

    return { contractList };
  };

  const normalizeYmd = (value, fallback) => {
    const ymd = String(value ?? "").replace(/\D/g, "");
    return /^\d{8}$/.test(ymd) ? ymd : fallback;
  };

  const createVirtualCalendarBase = () => {
    const units = createVirtualElevatorList();
    const statuses = ["접수", "배치", "도착", "완료"];
    const mainCategories = [
      "운행 중 정지",
      "문 열림/닫힘 이상",
      "소음 및 진동",
      "호출 불가",
      "층 표시 이상",
    ];
    const detailCategories = [
      "승객 갇힘 우려",
      "도어 센서 점검 필요",
      "제어반 알람 발생",
      "층 정지 오차",
      "안전장치 점검 필요",
    ];
    const daysTemplate = [3, 7, 12, 18, 24, 28];
    const selfInspectionDays = [2, 9, 16, 23];
    const list = [];
    let sequence = 1;

    for (let month = 1; month <= 12; month++) {
      const count = 2 + ((month * 7) % 4); // 2~5건
      const daysInMonth = new Date(2026, month, 0).getDate();

      for (let index = 0; index < count; index++) {
        const unit = units[(month * 5 + index) % units.length];
        const dayCandidate = daysTemplate[(month + index) % daysTemplate.length];
        const day = Math.min(dayCandidate + (index % 2), daysInMonth);
        const targetDt = `2026${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}`;
        const status = statuses[(month + index) % statuses.length];
        const mainCategory = mainCategories[(month + index) % mainCategories.length];
        const detailCategory = detailCategories[(month + index) % detailCategories.length];
        const acptNo = `ACPT-${targetDt}-${String(sequence).padStart(3, "0")}`;

        list.push({
          id: acptNo,
          acptNo,
          dtype: "FAULT_HIS",
          dtypeText: "고장 신고",
          targetDt,
          prjNm: unit.buldNm,
          intgPrjNo: unit.prjNo,
          hoNo: unit.hoNo,
          status,
          conslMcdNm: mainCategory,
          conslScdNm: detailCategory,
          bkdnLocLcdNm: "승강로",
          bkdnLocMcdNm: "도어 시스템",
          bkdnLocScdNm: "센서",
          bkdnCauCdNm: "노후 및 마모",
          procRsltCdNm: status === "완료" ? "처리 완료" : "처리 진행 중",
          carNo: `${unit.prjNo}${unit.hoNo}`,
        });
        sequence += 1;
      }

      // 2026년 정기검사: 월 1건
      {
        const unit = units[(month * 3) % units.length];
        const day = Math.min(6 + (month % 3), daysInMonth);
        const targetDt = `2026${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}`;
        const acptNo = `INSP-${targetDt}-${String(sequence).padStart(3, "0")}`;

        list.push({
          id: acptNo,
          acptNo,
          dtype: "INSP_HIS",
          dtypeText: "정기 검사",
          targetDt,
          prjNm: unit.buldNm,
          intgPrjNo: unit.prjNo,
          hoNo: unit.hoNo,
          status: "정기검사 완료",
        });
        sequence += 1;
      }

      // 2026년 정기점검: 월 4건
      for (let index = 0; index < 4; index++) {
        const unit = units[(month * 7 + index) % units.length];
        const day = Math.min(selfInspectionDays[index], daysInMonth);
        const targetDt = `2026${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}`;
        const acptNo = `SELF-${targetDt}-${String(sequence).padStart(3, "0")}`;

        list.push({
          id: acptNo,
          acptNo,
          dtype: "SELF_INSP_HIS",
          dtypeText: "정기 점검",
          targetDt,
          prjNm: unit.buldNm,
          intgPrjNo: unit.prjNo,
          hoNo: unit.hoNo,
          status: "정기점검 완료",
        });
        sequence += 1;
      }
    }

    return list.sort((a, b) => String(a.targetDt).localeCompare(String(b.targetDt)));
  };

  const createVirtualCalendarEvents = (queryparam = {}) => {
    const allEvents = createVirtualCalendarBase();
    const startDate = normalizeYmd(queryparam.startDate, "20260101");
    const endDate = normalizeYmd(queryparam.endDate, "20261231");

    return allEvents.filter(
      (item) => item.targetDt >= startDate && item.targetDt <= endDate
    );
  };

  const createVirtualTargetReception = (queryparam = {}) => {
    const targetAcptNo = String(queryparam.targetAcptNo ?? "").trim();
    if (!targetAcptNo) {
      return [];
    }

    const target = createVirtualCalendarBase().find(
      (item) => item.acptNo === targetAcptNo
    );
    if (!target) {
      return [];
    }

    return [
      {
        prjNm: target.prjNm,
        carNo: target.carNo,
        conslMcdNm: target.conslMcdNm,
        conslScdNm: target.conslScdNm,
        bkdnLocLcdNm: target.bkdnLocLcdNm,
        bkdnLocMcdNm: target.bkdnLocMcdNm,
        bkdnLocScdNm: target.bkdnLocScdNm,
        bkdnCauCdNm: target.bkdnCauCdNm,
        procRsltCdNm: target.procRsltCdNm,
      },
    ];
  };

  const createVirtualAiReportList = (queryparam = {}) => {
    const units = createVirtualElevatorList();
    const list = [];

    for (let month = 1; month <= 12; month++) {
      const unit = units[(month * 2) % units.length];
      const day = Math.min(10 + (month % 4), new Date(2026, month, 0).getDate());
      const chkDate = `2026${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}`;
      const hoNo3 = String(Number(unit.hoNo)).padStart(3, "0");

      list.push({
        elvtrDivNm: unit.elvtrDiv === "L" ? "엘리베이터" : "에스컬레이터",
        buldNm: unit.buldNm,
        elevatorNo: unit.elevatorNo,
        chkDate,
        projNo: `${unit.prjNo}${hoNo3}`,
      });
    }

    const startDate = normalizeYmd(queryparam.startDate, "20260101");
    const endDate = normalizeYmd(queryparam.endDate, "20261231");
    const keyword = String(queryparam.keyword ?? "")
      .trim()
      .toLowerCase();

    let filtered = list.filter(
      (item) => item.chkDate >= startDate && item.chkDate <= endDate
    );

    if (keyword) {
      filtered = filtered.filter((item) =>
        [item.projNo, item.elevatorNo, item.buldNm].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(keyword)
        )
      );
    }

    return filtered.sort((a, b) => String(b.chkDate).localeCompare(String(a.chkDate)));
  };

  const resolveUnitByProjAndHo = (projNo, hoNo) => {
    const normalizedProjNo = String(projNo ?? "").trim();
    const normalizedHoNo = String(Number(hoNo ?? 0)).padStart(2, "0");

    return (
      createVirtualElevatorList().find(
        (item) => item.prjNo === normalizedProjNo && item.hoNo === normalizedHoNo
      ) ?? createVirtualElevatorList()[0]
    );
  };

  const createVirtualAiMaster = (queryparam = {}) => {
    const unit = resolveUnitByProjAndHo(queryparam.projNo, queryparam.hoNo);
    const hoNo3 = String(Number(unit.hoNo)).padStart(3, "0");

    return {
      elProjNo: `${unit.prjNo}${hoNo3}`,
      dongcarNm: `${unit.hoNo}호기`,
      setupDate: unit.installationDe,
      model: unit.model,
      address: unit.address1,
    };
  };

  const createVirtualPerformanceResult = () => {
    return {
      ftotalExe: 95.6,
      stotalExe: 96.2,
      frst: "A",
      srst: "A",
      flevelNr: 94.1,
      slevelNr: 95.0,
      fopenCv: 93.4,
      sopenCv: 94.2,
      fcloseCv: 92.8,
      scloseCv: 93.7,
    };
  };

  const createVirtualRunInfo = (queryparam = {}) => {
    const requestDate = normalizeYmd(queryparam.requestDate, "20261201");
    const baseYear = Number(requestDate.slice(0, 4));
    const baseMonth = Number(requestDate.slice(4, 6));

    const target = [];
    const type = [];

    for (let offset = 5; offset >= 0; offset--) {
      const current = new Date(baseYear, baseMonth - 1 - offset, 1);
      const yyyy = String(current.getFullYear());
      const mm = String(current.getMonth() + 1).padStart(2, "0");
      const seed = offset + 1;

      target.push({
        yyyy,
        mm,
        elAvgRunDistance: String(980 + seed * 24),
        elAvgRunTime: String((7.1 + seed * 0.12).toFixed(2)),
        elAvgRunNum: String(126 + seed * 3),
        elAvgDocc: String(242 + seed * 5),
      });

      type.push({
        yyyy,
        mm,
        totAvgRunDistance: String(930 + seed * 19),
        totAvgRunTime: String((6.8 + seed * 0.1).toFixed(2)),
        totAvgRunNum: String(118 + seed * 2),
        totAvgDocc: String(220 + seed * 4),
      });
    }

    return { target, type };
  };

  const createVirtualRunTimeAverage = () => {
    return [
      {
        elAvgRunTime: "7.6",
      },
    ];
  };

  const createVirtualInspectionReportList = (queryparam = {}) => {
    const startDate = normalizeYmd(queryparam.startDate, "20260101");
    const endDate = normalizeYmd(queryparam.endDate, "20261231");
    const inspType = String(queryparam.inspType ?? "all").toLowerCase();
    const keyword = String(queryparam.keyword ?? "")
      .trim()
      .toLowerCase();

    let rows = createVirtualCalendarBase()
      .filter((item) => item.dtype === "INSP_HIS" || item.dtype === "SELF_INSP_HIS")
      .filter((item) => item.targetDt >= startDate && item.targetDt <= endDate)
      .map((item, index) => {
        const unit = createVirtualElevatorList().find(
          (el) => el.prjNo === item.intgPrjNo && el.hoNo === item.hoNo
        );
        const resolvedUnit = unit ?? createVirtualElevatorList()[index % createVirtualElevatorList().length];
        const reportTypeCode = item.dtype === "INSP_HIS" ? "INSP" : "SELF_INSP";
        const isPass = reportTypeCode === "INSP" ? index % 3 !== 0 : true;

        return {
          id: item.id,
          elvtrDivNm: resolvedUnit.elvtrDiv === "L" ? "엘리베이터" : "에스컬레이터",
          buldNm: resolvedUnit.buldNm,
          projNo: `${resolvedUnit.prjNo}${resolvedUnit.hoNo}`,
          reportTypeCode,
          reportType: reportTypeCode === "INSP" ? "정기검사" : "정기점검",
          chkDate: item.targetDt,
          endDate: item.targetDt,
          chkResult: reportTypeCode === "INSP" ? (isPass ? "합격" : "불합격") : "점검완료",
          failCd: `FAIL-${item.targetDt}-${String(index + 1).padStart(3, "0")}`,
          yyyymm: item.targetDt.slice(0, 6),
          elevatorNo: resolvedUnit.elevatorNo,
        };
      });

    if (inspType === "insp") {
      rows = rows.filter((item) => item.reportTypeCode === "INSP");
    }
    if (inspType === "selfinsp") {
      rows = rows.filter((item) => item.reportTypeCode === "SELF_INSP");
    }

    if (keyword) {
      rows = rows.filter((item) =>
        [item.projNo, item.elevatorNo, item.buldNm].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(keyword)
        )
      );
    }

    return rows.sort((a, b) => String(b.chkDate).localeCompare(String(a.chkDate)));
  };

  const createVirtualInspectionDetail = (queryparam = {}) => {
    const failCd = String(queryparam.failCd ?? "");
    const seed = Number(String(failCd).replace(/\D/g, "").slice(-2) || "1");
    const details = [
      {
        standardArticle: "제1조",
        standardTitle1: "권상기 이상 유무 점검",
        failDesc: seed % 2 === 0 ? "진동 기준치 초과" : "정상 범위",
        failDescInspector: seed % 2 === 0 ? "베어링 점검 및 윤활 필요" : "이상 없음",
      },
      {
        standardArticle: "제2조",
        standardTitle1: "도어 동작 상태 점검",
        failDesc: "닫힘 시간 편차",
        failDescInspector: "도어 클로저 재조정 필요",
      },
      {
        standardArticle: "제3조",
        standardTitle1: "비상통화 장치 점검",
        failDesc: "정상",
        failDescInspector: "이상 없음",
      },
    ];
    return details;
  };

  const createVirtualSelfInspectionDetail = (queryparam = {}) => {
    const yyyymm = String(queryparam.selChkBeginDate ?? "202601");
    const elevatorNo = String(queryparam.elevatorNo ?? "");
    return [
      {
        titNo: "1",
        selChkItemNm: "제어반 표시 상태",
        selChkItemDtlNm: `${yyyymm} ${elevatorNo} 표시등/알람 상태 점검`,
        selchkResultNm: "양호",
      },
      {
        titNo: "2",
        selChkItemNm: "도어 센서 동작",
        selChkItemDtlNm: "도어 개폐 및 센서 반응 점검",
        selchkResultNm: "양호",
      },
      {
        titNo: "3",
        selChkItemNm: "비상 정지 스위치",
        selChkItemDtlNm: "비상 정지 및 복구 동작 확인",
        selchkResultNm: "양호",
      },
    ];
  };

  const createVirtualInspectionMaster = (queryparam = {}) => {
    const elevatorNo = String(queryparam.elevatorNo ?? "");
    const unit =
      createVirtualElevatorList().find((item) => item.elevatorNo === elevatorNo) ??
      createVirtualElevatorList()[0];
    return {
      elevatorNo: unit.elevatorNo,
      buldNm: unit.buldNm,
      installationPlace: unit.installationPlace,
      prjNo: unit.prjNo,
      installationDe: unit.installationDe,
      selfCompany: unit.inspCompanyNm,
      address1: unit.address1,
    };
  };

  const createVirtualUserList = (queryparam = {}) => {
    const firstNames = [
      "민준",
      "서연",
      "도윤",
      "지우",
      "하준",
      "예은",
      "시우",
      "서준",
      "지민",
      "유진",
      "준호",
      "수빈",
      "현우",
      "나연",
      "정우",
      "가은",
      "태윤",
      "소민",
      "건우",
      "채원",
    ];
    const roles = ["SYSTEM", "ACCOUNT", "ENGINEER", "MAJOR", "MANAGER", "USER"];
    const statuses = ["0001", "0010", "0020", "0040", "4000"];
    const units = createVirtualElevatorList();
    const users = [];

    for (let idx = 0; idx < 20; idx++) {
      const seq = idx + 1;
      const unit = units[idx % units.length];
      const roleType = roles[idx % roles.length];
      const accountStatus = statuses[idx % statuses.length];
      const hasContract = idx % 5 !== 0;
      const userId = `user${String(seq).padStart(2, "0")}@miri.test`;
      const userName = firstNames[idx % firstNames.length];
      const phoneMid = String(1000 + ((idx * 137) % 9000)).padStart(4, "0");
      const phoneLast = String(1000 + ((idx * 263) % 9000)).padStart(4, "0");
      const y = 2025 + Math.floor(idx / 12);
      const m = String((idx % 12) + 1).padStart(2, "0");
      const d = String(((idx * 3) % 28) + 1).padStart(2, "0");

      users.push({
        userId,
        userName,
        phonenumber: `010${phoneMid}${phoneLast}`,
        roleType,
        accountStatus,
        delYn: idx % 9 === 0 ? "y" : "n",
        compsCntrNo: hasContract ? unit.contractCode : null,
        buildNm: hasContract ? unit.buldNm : "-",
        creationDt: `${y}${m}${d}`,
        empId: `EMP${String(1000 + seq)}`,
        intgPrjTrlineCdCode: hasContract ? unit.intgPrjTrlineCdCode : "-",
      });
    }

    const keyword = String(queryparam.searchKeyword ?? "")
      .trim()
      .toLowerCase();
    const keyword2 = String(queryparam.searchKeyword2 ?? "")
      .trim()
      .toLowerCase();
    const targetRoleType = String(queryparam.targetRoleType ?? "ALL")
      .trim()
      .toUpperCase();
    const accountStatus = String(queryparam.accountStatus ?? "ALL")
      .trim()
      .toUpperCase();
    const delYn = String(queryparam.delYn ?? "ALL")
      .trim()
      .toUpperCase();
    const contractYn = String(queryparam.contractYn ?? "ALL")
      .trim()
      .toUpperCase();
    const selectedUserId = String(queryparam.selectedUserId ?? "").trim().toLowerCase();

    let filtered = users;

    if (keyword) {
      filtered = filtered.filter((item) =>
        [item.userId, item.userName].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(keyword)
        )
      );
    }
    if (keyword2) {
      filtered = filtered.filter((item) =>
        [item.compsCntrNo, item.buildNm, item.empId].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(keyword2)
        )
      );
    }
    if (targetRoleType && targetRoleType !== "ALL") {
      filtered = filtered.filter(
        (item) => String(item.roleType).toUpperCase() === targetRoleType
      );
    }
    if (accountStatus && accountStatus !== "ALL") {
      filtered = filtered.filter(
        (item) => String(item.accountStatus).toUpperCase() === accountStatus
      );
    }
    if (delYn && delYn !== "ALL") {
      filtered = filtered.filter(
        (item) => String(item.delYn).toUpperCase() === delYn
      );
    }
    if (contractYn === "Y") {
      filtered = filtered.filter((item) => !!item.compsCntrNo);
    }
    if (contractYn === "N") {
      filtered = filtered.filter((item) => !item.compsCntrNo);
    }
    if (selectedUserId) {
      filtered = filtered.filter(
        (item) => String(item.userId).toLowerCase() !== selectedUserId
      );
    }

    return filtered;
  };

  const createVirtualUnitsTree = () => {
    const units = createVirtualElevatorList();
    const roots = [];
    const children = [];
    const rootByPrj = new Map();

    units.forEach((unit) => {
      if (!rootByPrj.has(unit.prjNo)) {
        const rootId = `SITE-${unit.prjNo}`;
        rootByPrj.set(unit.prjNo, rootId);
        roots.push({
          id: rootId,
          parentId: 0,
          type: "site",
          name: unit.buldNm,
          buldNm: unit.buldNm,
        });
      }

      children.push({
        id: unit.elevatorNo,
        parentId: rootByPrj.get(unit.prjNo),
        type: "el",
        installationPlace: unit.installationPlace,
        buldNm: unit.buldNm,
      });
    });

    return [...roots, ...children];
  };

  const createVirtualPartsPredictData = (queryparam = {}) => {
    const units = createVirtualElevatorList();
    const requested = Array.isArray(queryparam.projnos)
      ? queryparam.projnos
      : [];
    const targets =
      requested.length > 0
        ? units.filter((unit) => requested.includes(unit.elevatorNo))
        : units;

    const partsBlueprint = [
      {
        mgroup: "MAIN ROPE",
        criteria: "Count",
        limit: 600000,
        replacement_cycle: 5,
      },
      {
        mgroup: "MAIN INV",
        criteria: "RunTime",
        limit: 120000,
        replacement_cycle: 6,
      },
      {
        mgroup: "MAIN SHEAVE",
        criteria: "Count",
        limit: 500000,
        replacement_cycle: 6,
      },
      {
        mgroup: "SAFETY EDGE",
        criteria: "ElapsedTime",
        limit: 7,
        replacement_cycle: 4,
      },
      {
        mgroup: "DOOR INV",
        criteria: "RunTime",
        limit: 90000,
        replacement_cycle: 5,
      },
    ];

    const projnos = targets.map((unit, unitIndex) => {
      const materials = partsBlueprint.map((part, partIndex) => {
        const overRatio = 1.18 + ((unitIndex + partIndex) % 4) * 0.07;
        const accumulated_move = Math.round(part.limit * overRatio);
        const yearsElapsed = part.replacement_cycle + 1 + ((unitIndex + partIndex) % 2);
        const replacementDate = dayjs("2026-12-31")
          .subtract(yearsElapsed, "year")
          .format("YYYY-MM-DD");

        return {
          mgroup: part.mgroup,
          accumulated_move,
          comment: "수명 도래(교체 권장)",
          replacement_date: replacementDate,
          days_elapsed_installation: yearsElapsed * 365,
          standard_durability: {
            criteria: part.criteria,
            limit: part.limit,
            average_limit: part.limit,
            replacement_cycle: part.replacement_cycle,
          },
        };
      });

      return {
        projno: unit.elevatorNo,
        buldNm: unit.buldNm,
        materials,
      };
    });

    return { projnos };
  };

  const createVirtualFailRemarks = (queryparam = {}) => {
    const target = Array.isArray(queryparam.prjhno) ? queryparam.prjhno : [];
    if (!target.length) {
      return [];
    }
    return [
      { REMAKER: "로프 마모율 증가로 교체 권고" },
      { REMAKER: "도어 인버터 과열 이력 존재" },
      { REMAKER: "안전엣지 반응 지연 점검 필요" },
    ];
  };

  const createVirtualPurchaseList = (queryparam = {}) => {
    const units = createVirtualElevatorList();
    const startDate = normalizeYmd(queryparam.startDate, "20250101");
    const endDate = normalizeYmd(queryparam.endDate, "20261231");
    const keyword = String(queryparam.keyword ?? "")
      .trim()
      .toLowerCase();
    const list = [];

    for (let i = 0; i < 50; i++) {
      const unit = units[i % units.length];
      const month = (i % 12) + 1;
      const day = ((i * 3) % 27) + 1;
      const chngDt = `2026${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}`;
      const wbsNo = `WBS-${unit.prjNo}-${unit.hoNo}-${String(i + 1).padStart(3, "0")}`;

      list.push({
        wbsNo,
        materCont: unit.buldNm,
        chngDt,
        elevatorNo: unit.elevatorNo,
        frstInstallationDe: unit.frstInstallationDe,
        applcEnDt: unit.applcEnDt,
      });
    }

    let filtered = list.filter(
      (item) => item.chngDt >= startDate && item.chngDt <= endDate
    );

    if (keyword) {
      filtered = filtered.filter((item) =>
        [item.wbsNo, item.elevatorNo, item.materCont].some((value) =>
          String(value ?? "")
            .toLowerCase()
            .includes(keyword)
        )
      );
    }

    return filtered.sort((a, b) => String(b.chngDt).localeCompare(String(a.chngDt)));
  };

  const createVirtualPurchaseHistory = (queryparam = {}) => {
    const wbsNo = String(queryparam.wbsNo ?? "").trim();
    const chngDt = normalizeYmd(queryparam.chngDt, "20260101");
    if (!wbsNo) {
      return [];
    }

    const parts = [
      { materNo: "PRT-ROPE-001", materCont: "메인로프", materVol: "4EA", materSize: "12mm" },
      { materNo: "PRT-INV-002", materCont: "메인 인버터", materVol: "1EA", materSize: "380V" },
      { materNo: "PRT-SHV-003", materCont: "메인 쉬브", materVol: "1EA", materSize: "480mm" },
      { materNo: "PRT-SED-004", materCont: "세이프티 엣지", materVol: "2EA", materSize: "1200mm" },
      { materNo: "PRT-DINV-005", materCont: "도어 인버터", materVol: "1EA", materSize: "220V" },
    ];

    const seed = Number(String(wbsNo).replace(/\D/g, "").slice(-2) || "1");
    return parts.slice(0, 3 + (seed % 3)).map((part, idx) => ({
      ...part,
      wbsNo,
      chngDt,
      materNo: `${part.materNo}-${String((seed + idx) % 10).padStart(2, "0")}`,
    }));
  };

  const createVirtualServiceRequestSeed = () => {
    if (Array.isArray(virtualServiceRequestCache)) {
      return virtualServiceRequestCache;
    }

    const units = createVirtualElevatorList();
    const serviceTypes = [
      { code: "0010", value1: "고장신고" },
      { code: "0020", value1: "청구서 재발행" },
      { code: "0030", value1: "청구서 수신 변경" },
      { code: "0040", value1: "요금 계산/요율 문의" },
      { code: "0050", value1: "납부 방법 변경" },
      { code: "0060", value1: "고객정보 변경" },
    ];

    const list = [];
    for (let idx = 0; idx < 50; idx++) {
      const unit = units[idx % units.length];
      const service = serviceTypes[idx % serviceTypes.length];
      const month = (idx % 12) + 1;
      const day = ((idx * 2) % 27) + 1;
      const registDt = `2026${String(month).padStart(2, "0")}${String(day).padStart(2, "0")}${String(9 + (idx % 8)).padStart(2, "0")}30`;

      list.push({
        id: `SR-${String(idx + 1).padStart(4, "0")}`,
        registDt,
        serviceType: service.code,
        serviceNm: service.value1,
        serviceContents: `${unit.buldNm} ${unit.hoNo}호기 관련 ${service.value1} 요청`,
        receiverNm: `담당자${String((idx % 9) + 1)}`,
        receiverEmail: `receiver${String((idx % 9) + 1)}@miri.test`,
        prjNo: unit.prjNo,
        hoNo: unit.hoNo,
        elevatorNo: unit.elevatorNo,
        frstInstallationDe: unit.frstInstallationDe,
      });
    }

    virtualServiceRequestCache = list.sort((a, b) =>
      String(b.registDt).localeCompare(String(a.registDt))
    );
    return virtualServiceRequestCache;
  };

  const createVirtualServiceList = (queryparam = {}) => {
    const startDate = normalizeYmd(queryparam.startDate, "20250101");
    const endDate = normalizeYmd(queryparam.endDate, "20261231");
    const serviceType = String(queryparam.serviceType ?? "ALL")
      .trim()
      .toUpperCase();
    const searchKeyword = String(queryparam.searchKeyword ?? "")
      .trim()
      .toLowerCase();

    let list = createVirtualServiceRequestSeed().filter((item) => {
      const ymd = String(item.registDt).slice(0, 8);
      return ymd >= startDate && ymd <= endDate;
    });

    if (serviceType && serviceType !== "ALL") {
      list = list.filter(
        (item) => String(item.serviceType).toUpperCase() === serviceType
      );
    }

    if (searchKeyword) {
      list = list.filter((item) =>
        [item.serviceContents, item.prjNo, item.elevatorNo, item.receiverNm].some(
          (value) =>
            String(value ?? "")
              .toLowerCase()
              .includes(searchKeyword)
        )
      );
    }

    return list;
  };

  const createVirtualServiceRequest = (queryparam = {}) => {
    const base = createVirtualServiceRequestSeed();
    const now = dayjs();
    const prjNo = String(queryparam.prjNo ?? "").trim();
    const hoNo = String(queryparam.hoNo ?? "").padStart(2, "0");
    const serviceType = String(queryparam.serviceType ?? "0060");
    const serviceNm = String(queryparam.serviceTypeNm ?? "서비스 요청");
    const unit =
      createVirtualElevatorList().find(
        (item) => item.prjNo === prjNo && item.hoNo === hoNo
      ) ?? createVirtualElevatorList()[0];

    const newItem = {
      id: `SR-${String(base.length + 1).padStart(4, "0")}`,
      registDt: now.format("YYYYMMDDHHmm"),
      serviceType,
      serviceNm,
      serviceContents: String(queryparam.contents ?? "").trim(),
      receiverNm: "고객센터",
      receiverEmail: "service@miri.test",
      prjNo: unit.prjNo,
      hoNo: unit.hoNo,
      elevatorNo: unit.elevatorNo,
      frstInstallationDe: unit.frstInstallationDe,
    };

    virtualServiceRequestCache = [newItem, ...base];
    return { requestId: newItem.id };
  };

  const createMockDataByUrl = (params) => {
    const url = params?.url;
    const queryparam = params?.queryparam ?? {};

    if (url === "/v2/auth/my") {
      return {
        userId: "demo_user",
        userName: "Demo User",
        roleType: "SYSTEM",
        locale: "ko_kr",
        theme: "light",
        landingpageType: "map_page",
        phonenumber: "01012341234",
      };
    }

    if (url === "/v2/portfolio/current/list") {
      return [
        {
          userPortfolioMappingId: 1,
          portfolioName: "lobby",
          defaultYn: "y",
          portfolioInContractEa: 1,
        },
      ];
    }

    if (url === "/v2/alarm-setup/current/web") {
      return [
        {
          alarmRuleList: [],
        },
      ];
    }

    if (url === "/v2/masterdata/head/select") {
      return createMasterDataMock(queryparam.masterdataKey);
    }

    if (url === "/v2/weather/forecast") {
      return { list: createWeatherMockList() };
    }

    if (url === "/v2/hccc/current/run-count") {
      return {
        el: { run: 118, notRun: 7 },
        es: { run: 43, notRun: 2 },
      };
    }

    if (url === "/v2/hccc/current/run-avg/period") {
      return {
        month: "97.1",
        quarter: "96.4",
        halfyear: "95.7",
      };
    }

    if (url === "/v2/hccc/current/run-avg") {
      return {
        elAvg: 97.1,
        esAvg: 95.8,
      };
    }

    if (url === "/v2/hccc/current/count-info") {
      return {
        allCount: 20,
        failCount: 4,
      };
    }

    if (url === "/v2/hrts/car/distance/average") {
      return {
        elAvgRunDistance: 1284,
        startDate: "2026-01",
        endDate: "2026-02",
      };
    }

    if (url === "/v2/contract/current/companion-days") {
      return 120;
    }

    if (url === "/v2/contract/portfolio/details") {
      return createVirtualContractDetails(queryparam);
    }

    if (url === "/v2/contract/current/details" || url === "v2/contract/current/details") {
      return createVirtualContractCurrentDetails(queryparam);
    }

    if (url === "/v2/hrts/manage/master/list") {
      return createVirtualAiReportList(queryparam);
    }

    if (url === "/v2/hrts/manage/master/ai") {
      return createVirtualAiMaster(queryparam);
    }

    if (url === "/v2/hrts/car/performance-result") {
      return createVirtualPerformanceResult();
    }

    if (url === "/v2/hrts/car/run-info") {
      return createVirtualRunInfo(queryparam);
    }

    if (url === "/v2/hrts/car/run-time/average") {
      return createVirtualRunTimeAverage();
    }

    if (url === "/v2/cc/manage/integrated/insp/list") {
      return createVirtualInspectionReportList(queryparam);
    }

    if (url === "/v2/cc/manage/car/insp/details") {
      return createVirtualInspectionDetail(queryparam);
    }

    if (url === "/v2/cc/manage/car/self-insp/details") {
      return createVirtualSelfInspectionDetail(queryparam);
    }

    if (url === "/v2/cc/manage/integrated/master") {
      return createVirtualInspectionMaster(queryparam);
    }

    if (url === "/v2/user/list") {
      return createVirtualUserList(queryparam);
    }

    if (url === "/v2/cc/units/tree") {
      return createVirtualUnitsTree();
    }

    if (url === "/v2/unit-service/material/predict/projnos") {
      return createVirtualPartsPredictData(queryparam);
    }

    if (url === "/v2/hccc/current/fail-remarks") {
      return createVirtualFailRemarks(queryparam);
    }

    if (url === "/v2/unit-service/purchase/list") {
      return createVirtualPurchaseList(queryparam);
    }

    if (url === "/v2/unit-service/purchase/history") {
      return createVirtualPurchaseHistory(queryparam);
    }

    if (url === "/v2/cc/current/service") {
      return createVirtualServiceList(queryparam);
    }

    if (url === "/v2/cc/service/request") {
      return createVirtualServiceRequest(queryparam);
    }

    if (url === "/v2/hccc/current/map-view") {
      return createVirtualElevatorList();
    }

    if (url === "/v2/cc/current/elevator") {
      const list = createVirtualElevatorList();
      const prjNo = String(queryparam.prjNo ?? "").trim();
      const carNo = String(queryparam.carNo ?? "").trim();
      const normalizedCarNo = String(carNo).replace(/\D/g, "").padStart(2, "0");

      const matched = list.find((item) => {
        const hoNo = String(item.hoNo ?? "");
        const isPrjMatched = !prjNo || String(item.prjNo).includes(prjNo);
        const isCarMatched =
          !carNo ||
          hoNo === carNo ||
          hoNo === normalizedCarNo ||
          String(item.elevatorNo).includes(carNo);
        return isPrjMatched && isCarMatched;
      });

      return matched ?? null;
    }

    if (url === "/v2/cc/current/elevators") {
      let list = createVirtualElevatorList();
      const prjNo = String(queryparam.prjNo ?? "").trim();
      const carNo = String(queryparam.carNo ?? "").trim();
      const type = String(queryparam.type ?? "all").trim().toUpperCase();

      if (prjNo) {
        list = list.filter((item) => String(item.prjNo).includes(prjNo));
      }
      if (carNo) {
        list = list.filter(
          (item) =>
            String(item.hoNo).includes(carNo) ||
            String(item.elevatorNo).includes(carNo)
        );
      }
      if (type && type !== "ALL") {
        list = list.filter(
          (item) =>
            String(item.type).toUpperCase() === type ||
            String(item.elvtrDiv).toUpperCase() === type
        );
      }

      return list;
    }

    if (url === "/v2/hccc/current/fail-count") {
      return {
        failComplete: 8,
        failDetect: 3,
        fieldRepair: 2,
        remoteRepair: 3,
      };
    }

    if (url === "/v2/hccc/current/fail-status") {
      return [
        {
          text: "[Virtual Site 01] - 01호기 : 연결 대기",
          updDt: "20260211093000",
          activityStatus: "대기",
        },
        {
          text: "[Virtual Site 03] - 03호기 : 원격 처리 중",
          updDt: "20260211102000",
          activityStatus: "처리중",
        },
      ];
    }

    if (url === "/v2/cc/portfolio/self-insp/count-info") {
      return {
        field: 5,
        remote: 7,
        success: 12,
        total: 20,
      };
    }

    if (
      url === "/v2/miri/view/callcount" ||
      url === "/v2/miri/robot/callcount" ||
      url === "/v2/miri/call/callcount"
    ) {
      return {
        callCount: 14,
      };
    }

    if (url === "/v2/unit-service/material/count-info") {
      return {
        allCount: 23,
        ropeCount: 5,
        sheaveCount: 4,
        mainInvCount: 6,
        doorInvCount: 4,
        edgeCount: 4,
        otherCount: 0,
      };
    }

    if (url === "/v2/contract/current/contact/list") {
      return [
        {
          deptNm: "Seoul Office",
          mob: "02-1234-5678",
        },
      ];
    }

    if (url === "/v2/cc/current/calender") {
      return createVirtualCalendarEvents(queryparam);
    }

    if (url === "/v2/hccc/current/targetReception") {
      return createVirtualTargetReception(queryparam);
    }

    return [];
  };

  const request = async (params = {}) => {
    const method = String(params.method ?? "get").toLowerCase();
    const url = String(params.url ?? "");
    const queryparam = params.queryparam ?? {};
    const isPrivilegeApi =
      url.startsWith("/v2/privilege/") || url === "/v2/user/list";

    if (usePrivilegeApi && isPrivilegeApi && privilegeApiBaseUrl) {
      try {
        const requestUrl = new URL(url, privilegeApiBaseUrl);
        const fetchOptions = {
          method: method.toUpperCase(),
          headers: {
            "Content-Type": "application/json",
          },
        };

        if (method === "get") {
          Object.entries(queryparam).forEach(([key, value]) => {
            if (value !== undefined && value !== null) {
              requestUrl.searchParams.append(key, String(value));
            }
          });
        } else {
          fetchOptions.body = JSON.stringify(queryparam);
        }

        const response = await fetch(requestUrl.toString(), fetchOptions);
        const payload = await response.json();

        // Worker response: { code, result, data, message }
        if (payload && typeof payload === "object" && "code" in payload) {
          return payload;
        }

        // Legacy response compatibility
        return {
          code: response.status,
          result: response.ok ? "success" : "fail",
          data: payload?.response?.data ?? payload?.data ?? payload,
          message: payload?.message ?? "",
          because: payload?.response?.because,
        };
      } catch (error) {
        return {
          code: 500,
          result: "fail",
          data: null,
          message: error?.message ?? "Privilege API request failed",
        };
      }
    }

    if (shouldShowMockModal) {
      useUiStore.showMockModal({
        url,
        method,
        data: queryparam,
      });
    }

    return {
      code: 200,
      result: "success",
      data: createMockDataByUrl({ ...params, method, url, queryparam }),
      message: "Demo Mode: API Intercepted",
    };
  };

  return {
    request,
  };
});





